import { useId, useState } from 'react';
import {
  CheckOutlined,
  CloseOutlined,
  FileOutlined,
  InboxOutlined,
  SendOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Upload } from 'antd';

import { PageHeader } from '../../../components/shared';
import { Badge, Button, Empty, Field, Input, Select, Textarea } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { dungLuongVN, soVN } from '../../../lib/dinh-dang';
import { loiTheoTruong, maLoi } from '../../../lib/errors';
import { CanhBaoTrongHop, LoiTrongHop } from '../../tenant/components/phan-form';
import { KHOA_LOI_TEP, QUYEN, TRANG_THAI_MUC } from '../constants';
import { DAI_TIEU_DE, useHangDoiTaiLieu } from '../hooks/useHangDoiTaiLieu';
import type { MucHangDoi } from '../hooks/useHangDoiTaiLieu';
import { useTatCaKho } from '../hooks/useTriThuc';
import { DUOI_NHAN, TOI_DA_MB } from '../nghiep-vu/tri-thuc';
import type { LoiTep } from '../nghiep-vu/tri-thuc';

const DAI_TOM_TAT = 20_000;

/**
 * Upload tài liệu (`/tri-thuc/upload`, phân hệ `KNOWLEDGE_UPLOAD`): kéo thả tệp vào hàng đợi, chọn bộ tri
 * thức và tiêu đề cho từng tệp, rồi gửi thành tài liệu **nháp** của luồng kiểm duyệt. Tài liệu chỉ thành
 * tệp trong bộ (và vào index) khi được duyệt ở màn "Kiểm duyệt & Phê duyệt".
 *
 * Khác native: backend platform không nhận khoảng hiệu lực khi tạo tài liệu, nên màn này không hỏi; hiệu
 * lực đặt ở tệp sau khi duyệt (màn chi tiết bộ).
 */
export function UploadTaiLieuView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();
  const duocTao = useModulePermission(QUYEN.taiLieuTao);
  const kho = useTatCaKho();
  const hang = useHangDoiTaiLieu();
  const [tuChoi, setTuChoi] = useState<Array<{ ten: string; ma: LoiTep }>>([]);
  const idTieuDeHang = useId();

  const tieuDe = t('nav.muc.uploadTaiLieu');
  const conGui = hang.ds.filter((m) => m.trangThai === 'cho' || m.trangThai === 'loi').length;
  const daXong = hang.ds.filter((m) => m.trangThai === 'xong').length;
  const tuyChonBo = (kho.data ?? []).map((k) => ({ value: k.id, label: `${k.name} (${k.code})` }));

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.triThucAi') },
            { label: t('nav.muc.cauHinhDauVaoTriThuc') },
            { label: tieuDe },
          ]}
          titleIcon={<UploadOutlined className="text-primary-600" />}
          title={tieuDe}
          subtitle={t('triThuc.upload.moTa')}
          actions={
            <Button variant="outline" size="md" onClick={() => dieuHuong('/tri-thuc/kiem-duyet')}>
              {t('triThuc.upload.moKiemDuyet')}
            </Button>
          }
        />

        {!duocTao && <CanhBaoTrongHop>{t('triThuc.upload.khongCoQuyen')}</CanhBaoTrongHop>}

        {duocTao && (
          <div className="space-y-2">
            <Upload.Dragger
              multiple
              accept={DUOI_NHAN.join(',')}
              showUploadList={false}
              disabled={hang.dangChay}
              beforeUpload={(tep) => {
                const ma = hang.them(tep);
                if (ma) setTuChoi((cu) => [...cu, { ten: tep.name, ma }]);
                return Upload.LIST_IGNORE;
              }}
            >
              <p className="text-3xl text-primary-600">
                <InboxOutlined aria-hidden />
              </p>
              <p className="text-sm font-medium text-content-primary">{t('triThuc.tep.keoTha')}</p>
              <p className="text-xs text-content-muted">{t('triThuc.tep.keoThaMo', { so: soVN(TOI_DA_MB, locale) })}</p>
            </Upload.Dragger>
            {tuChoi.length > 0 && (
              <LoiTrongHop>
                <span className="block">{t('triThuc.tep.khongNhan')}</span>
                {tuChoi.map((x, i) => (
                  <span key={i} className="block break-all">
                    {t('triThuc.tep.tepVaLyDo', { ten: x.ten, lyDo: t(KHOA_LOI_TEP[x.ma], { so: soVN(TOI_DA_MB, locale) }) })}
                  </span>
                ))}
                <button type="button" className="mt-1 underline" onClick={() => setTuChoi([])}>
                  {t('triThuc.upload.anThongBao')}
                </button>
              </LoiTrongHop>
            )}
          </div>
        )}

        <section aria-labelledby={idTieuDeHang} className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          <div className="flex flex-wrap items-center gap-3 border-b border-default px-4 py-3">
            <h2 id={idTieuDeHang} className="text-sm font-semibold text-content-primary">
              {t('triThuc.upload.hangDoi')}
            </h2>
            <Badge variant={hang.ds.length > 0 ? 'primary' : 'neutral'}>{soVN(hang.ds.length, locale)}</Badge>
            <div className="flex-1" />
            {daXong > 0 && (
              <Button variant="ghost" size="sm" icon={<CheckOutlined />} onClick={hang.boDaXong} disabled={hang.dangChay}>
                {t('triThuc.upload.boDaXong', { so: soVN(daXong, locale) })}
              </Button>
            )}
            {conGui > 1 && (
              <Select<string>
                showSearch={{ optionFilterProp: 'label' }}
                className="min-w-[14rem]"
                placeholder={t('triThuc.upload.datBoTatCa')}
                aria-label={t('triThuc.upload.datBoTatCa')}
                loading={kho.isLoading}
                options={tuyChonBo}
                value={null}
                onChange={(v) => hang.datBoChoTatCa(v)}
                disabled={hang.dangChay}
              />
            )}
            {duocTao && conGui > 0 && (
              <Button variant="primary" size="md" icon={<SendOutlined />} loading={hang.dangChay} onClick={() => void hang.guiTatCa()}>
                {hang.dangChay ? t('triThuc.tep.dangTaiLen') : t('triThuc.upload.guiTatCa', { so: soVN(conGui, locale) })}
              </Button>
            )}
          </div>

          {kho.isError && (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 border-b border-default px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">{t('triThuc.loi.danhSachBo', { loi: dichLoi(kho.error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void kho.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          )}
          {kho.data && kho.data.length === 0 && hang.ds.length > 0 && (
            <div className="border-b border-default px-4 py-3">
              <CanhBaoTrongHop>{t('triThuc.upload.chuaCoBo')}</CanhBaoTrongHop>
            </div>
          )}

          {hang.ds.length === 0 ? (
            <Empty icon={InboxOutlined} title={t('triThuc.upload.rong')} description={duocTao ? t('triThuc.upload.rongMo') : undefined} />
          ) : (
            <ul className="divide-y divide-default">
              {hang.ds.map((m) => (
                <MucHang
                  key={m.khoa}
                  m={m}
                  tuyChonBo={tuyChonBo}
                  dangTaiBo={kho.isLoading}
                  khoa={hang.dangChay}
                  onSua={(doi) => hang.sua(m.khoa, doi)}
                  onBo={() => hang.bo(m.khoa)}
                  onGui={() => void hang.guiMot(m.khoa)}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function MucHang({
  m,
  tuyChonBo,
  dangTaiBo,
  khoa,
  onSua,
  onBo,
  onGui,
}: {
  m: MucHangDoi;
  tuyChonBo: Array<{ value: string; label: string }>;
  dangTaiBo: boolean;
  /** Hàng đợi đang gửi: khoá sửa và bỏ mục. */
  khoa: boolean;
  onSua: (doi: Partial<MucHangDoi>) => void;
  onBo: () => void;
  onGui: () => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const trangThai = TRANG_THAI_MUC[m.trangThai];
  const suaDuoc = m.trangThai === 'cho' || m.trangThai === 'loi';

  // Lỗi của server: trùng tiêu đề đặt dưới ô tiêu đề, lỗi theo trường đặt dưới ô của nó, còn lại để chung.
  const truong = loiTheoTruong(m.loi);
  const trungTieuDe = maLoi(m.loi) === 'TRI_THUC_DOCUMENT_TITLE_EXISTS' ? dichLoi(m.loi) : undefined;
  const loiTieuDe = (m.daGui && !m.tieuDe.trim() ? t('triThuc.upload.loiTieuDeTrong') : undefined)
    ?? (m.tieuDe.trim().length > DAI_TIEU_DE ? t('triThuc.upload.loiTieuDeDai', { so: soVN(DAI_TIEU_DE, locale) }) : undefined)
    ?? trungTieuDe ?? truong['/title'];
  const loiBo = (m.daGui && !m.kbId ? t('triThuc.upload.loiChuaChonBo') : undefined) ?? truong['/knowledge_base_id'];
  const loiChung = m.loi && !trungTieuDe && Object.keys(truong).length === 0 ? dichLoi(m.loi) : undefined;

  return (
    <li className="space-y-3 px-4 py-4">
      <div className="flex items-center gap-3">
        <FileOutlined aria-hidden className="shrink-0 text-content-muted" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-content-primary" title={m.tep.name}>
          {m.tep.name}
        </span>
        <span className="shrink-0 text-xs tabular-nums text-content-muted">{dungLuongVN(m.tep.size, locale)}</span>
        <Badge variant={trangThai.variant}>{t(trangThai.khoa)}</Badge>
        {suaDuoc && (
          <Button variant="outline" size="sm" icon={<SendOutlined />} disabled={khoa} onClick={onGui}>
            {t('triThuc.upload.guiMuc')}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          icon={<CloseOutlined />}
          disabled={khoa}
          title={t('triThuc.upload.boKhoiHang', { ten: m.tep.name })}
          aria-label={t('triThuc.upload.boKhoiHang', { ten: m.tep.name })}
          onClick={onBo}
        />
      </div>

      {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}

      {suaDuoc ? (
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={t('triThuc.upload.fBo')} required error={loiBo}>
            {(p) => (
              <Select<string>
                id={p.id}
                aria-describedby={p['aria-describedby']}
                status={p['aria-invalid'] ? 'error' : undefined}
                className="w-full"
                showSearch={{ optionFilterProp: 'label' }}
                placeholder={t('triThuc.chonBo')}
                loading={dangTaiBo}
                disabled={khoa}
                options={tuyChonBo}
                value={m.kbId}
                onChange={(v) => onSua({ kbId: v, loi: undefined })}
              />
            )}
          </Field>
          <Field label={t('triThuc.upload.fTieuDe')} required error={loiTieuDe}>
            {(p) => (
              <Input
                {...p}
                disabled={khoa}
                value={m.tieuDe}
                onChange={(e) => onSua({ tieuDe: e.target.value, loi: undefined })}
              />
            )}
          </Field>
          <Field className="md:col-span-2" label={t('triThuc.upload.fTomTat')} hint={t('triThuc.upload.fTomTatGoiY')}>
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                maxLength={DAI_TOM_TAT}
                disabled={khoa}
                value={m.tomTat}
                onChange={(e) => onSua({ tomTat: e.target.value })}
              />
            )}
          </Field>
        </div>
      ) : (
        <p className="truncate pl-7 text-xs text-content-secondary">{m.tieuDe}</p>
      )}
    </li>
  );
}
